import type { Deposit, DepositRefundResponse } from '@library/types/payments';
import { useMemo, useState } from 'react';
import { useRefundPaymentMutation } from '@bo-services/paymentsApi';

export const useDepositRefunds = (deposits: Deposit[]) => {
  const [refundDeposit] = useRefundPaymentMutation();
  const [refundStatuses, setRefundStatuses] = useState<
    Record<string, Partial<DepositRefundResponse & { error: string }>>
  >({});
  const [refunding, setRefunding] = useState(false);
  const processedIds = useMemo(() => new Set<string>(), []);

  const submitDepositsForRefund = async () => {
    if (deposits.length === 0 || refunding) {
      return;
    }

    const newRefundStatuses = { ...refundStatuses };

    setRefunding(true);

    // need to run this loop in this way to properly wait for calls
    // doing forEach and map async would not wait for the responses to come back before
    // initiating the next call
    // eslint-disable-next-line no-restricted-syntax
    for (const deposit of deposits) {
      const id = deposit.merchantReferenceId;

      if (!processedIds.has(id)) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const response = await refundDeposit({ id }).unwrap();
          // we have to update the refund status object with failure from this state

          newRefundStatuses[id] = response;
        } catch (error) {
          // we have to update the refund status object with failure from this state
          newRefundStatuses[id] = {
            status: 'failed',
            error: String(error)
          };
        }

        processedIds.add(id);
      }
    }

    setRefunding(false);
    setRefundStatuses(newRefundStatuses);
  };

  const refundsComplete = processedIds.size === deposits.length;

  const resetState = () => {
    setRefundStatuses({});
    setRefunding(false);
    processedIds.clear();
  };

  return {
    submitDepositsForRefund,
    refundStatuses,
    refundsComplete,
    refunding,
    resetState
  };
};
